import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { captureAttribution } from "@/lib/utm";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Booking from "@/pages/Booking";
import BookingDetail from "@/pages/BookingDetail";
import BookingSuccess from "@/pages/BookingSuccess";
import Account from "@/pages/Account";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLogin from "@/pages/AdminLogin";
import AuthCallback from "@/pages/AuthCallback";
import AirportTransfers from "@/pages/AirportTransfers";
import Corporate from "@/pages/Corporate";
import SeoLanding from "@/pages/SeoLanding";
import Weddings from "@/pages/Weddings";
import Groups from "@/pages/Groups";
import ServiceAreas from "@/pages/ServiceAreas";
import Portal from "@/pages/Portal";
import TripSheet from "@/pages/TripSheet";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-white" />
    </div>
  );
}

const STAFF_ROLES = ["admin", "dispatcher", "driver", "readonly"];

function ProtectedRoute({ children, adminOnly, staffOnly }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to={adminOnly || staffOnly ? "/admin-login" : "/login"} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/admin-login" replace />;
  if (staffOnly && !STAFF_ROLES.includes(user.role)) return <Navigate to="/admin-login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/airport-transfers" element={<AirportTransfers />} />
      <Route path="/corporate" element={<Corporate />} />
      <Route path="/weddings-events" element={<Weddings />} />
      <Route path="/group-transportation" element={<Groups />} />
      <Route path="/service-areas" element={<ServiceAreas />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/services/:slug" element={<SeoLanding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/book" element={<Booking />} />
      <Route path="/booking/success" element={<BookingSuccess />} />
      <Route path="/booking/:id" element={<BookingDetail />} />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute staffOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/trip-sheet/:id"
        element={
          <ProtectedRoute staffOnly>
            <TripSheet />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    captureAttribution();
  }, []);
  return (
    <div className="App grain min-h-screen">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="bottom-right" theme="dark" />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
